import {
  createEditor,
  Element,
  Editor,
  Text,
  Node,
  type Range,
  type Path,
  type NodeEntry,
} from 'slate';
import { ReactEditor, withReact } from 'slate-react';
import { isEqual } from 'lodash';
import AhaEditor from '../AhaEditor';
import type {
  DocumentValue,
  EditorMetadata,
  BackwardsCompatibleDocumentValue,
} from '../types/aha-editor';
import { findEndNodeIndex, findStartNodeIndex } from './util/findRangeForText';
import { MigrateToV2 } from '../migration';
import { HtmlSerializer } from '../serialization/HtmlSerializer';
import emptyParagraph from './util/emptyParagraph';
import PathUtils from './PathUtils';

interface siblingFindOpts {
  match?: (node: Node, path: Path) => boolean;
}

class EditorUtils {
  // Initialize the editor with with JSON, HTML or plain text/markdown. Pass
  // value for JSON or body for HTML/markdown.
  static initializeValue(
    value?: BackwardsCompatibleDocumentValue | null,
    html?: string | null
  ): DocumentValue {
    if (value) {
      return MigrateToV2(value);
    } else if (html) {
      const deserializer = new HtmlSerializer(undefined, {
        enableFontSizeMark: true,
        enableFontFamilyMark: true,
      });

      const value: DocumentValue = deserializer.deserialize(html);

      return value;
    } else {
      return this.emptyDocument();
    }
  }

  static emptyParagraph(): Element {
    return emptyParagraph();
  }

  static emptyDocument(): DocumentValue {
    return {
      children: [EditorUtils.emptyParagraph()],
      metadata: this.defaultMetadata(),
    };
  }

  static defaultMetadata(): EditorMetadata {
    return {
      annotations: {},
      tableOfContentsEnabled: false,
    };
  }

  static isEmpty(editor: Editor): boolean {
    return isEqual(editor.children, this.emptyDocument().children);
  }

  static createReactEditor() {
    const editor = createEditor();
    const reactEditor = withReact(editor, AhaEditor.clipboardFormatKey);

    reactEditor.children = EditorUtils.emptyDocument().children;

    return reactEditor;
  }

  static areEqual(first: Editor, second: Editor) {
    if (!isEqual(first.selection, second.selection)) return false;
    if (!isEqual(first.metadata, second.metadata)) return false;
    if (!isEqual(first.children, second.children)) return false;

    return true;
  }

  static firstElementWithProperties<T extends Node>(
    editor: Editor,
    properties: { [key: string]: string | number | boolean }
  ) {
    const [nodeEntry] = Editor.nodes<T>(editor, {
      at: [],
      match: (node) =>
        Element.isElement(node) &&
        Object.entries(properties).every(([k, v]) => node[k] === v),
      mode: 'highest',
    });
    return nodeEntry;
  }

  static focus(editor: Editor) {
    ReactEditor.focus(editor);
  }

  static blur(editor: Editor) {
    ReactEditor.blur(editor);
  }

  // Return the raw text in a range.
  static textInRange(editor: Editor, range: Range): string {
    try {
      return Editor.string(editor, range);
    } catch {
      // Editor.string internally uses Editor.nodes which can throw if
      // the content has changed.
      return '';
    }
  }

  // Find the range for the first occurrence of searchText in the editor document.
  // Attempts to find searchText even if it spans multiple text nodes.
  static findRangeForText(
    editor: Editor,
    searchText: string
  ): Range | undefined {
    // Get an array of all text nodes in the document
    const textNodes = Editor.nodes<Text>(editor, {
      at: [],
      match: Text.isText,
    });

    const nodeMap = new Map<
      number,
      {
        node: Text;
        path: Path;
        documentTextIndex: number;
      }
    >();
    let documentText = '';

    // Iterate over all text nodes in the document and create a string of
    // the entire document text. We also create a map of the text nodes
    // and their corresponding indices in the document text.
    for (const [node, path] of textNodes) {
      const documentTextIndex = documentText.length
        ? documentText.length - 1
        : 0;
      documentText += node.text.toLowerCase() + ' ';

      nodeMap.set(documentTextIndex, {
        node,
        path,
        documentTextIndex,
      });
    }

    // Return early if the document is empty.
    if (!documentText) return;

    // Find the first match of searchText in documentText.
    // Return early if no match is found.
    const searchTextRegex = new RegExp(
      searchText
        .toLowerCase()
        // Replace regex special chars
        .replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&')
        .split(/\s+/)
        .join('\\s+')
    );
    const match = documentText.match(searchTextRegex);
    if (!match) return;

    const matchIndex = match.index as number;
    const matchLength = match[0].length;
    const documentIndices = Array.from(nodeMap.keys()).sort((a, b) => a - b);

    // Find the start and end node indices for the match, return early if
    // we can't find the nodes.
    const startNodeIndex = findStartNodeIndex(documentIndices, matchIndex);
    if (startNodeIndex === -1) return;

    const endNodeIndex = findEndNodeIndex(
      documentIndices,
      startNodeIndex,
      matchIndex + matchLength
    );
    if (endNodeIndex === -1) return;

    const startNode = nodeMap.get(documentIndices[startNodeIndex]);
    const endNode = nodeMap.get(documentIndices[endNodeIndex]);

    if (!startNode || !endNode) {
      return;
    }

    // Once we have the start and end nodes, we can calculate the offsets
    // for the anchor and focus.
    const startOffset =
      matchIndex - startNode.documentTextIndex - (startNodeIndex > 0 ? 1 : 0);
    const endOffset =
      matchIndex +
      matchLength -
      endNode.documentTextIndex -
      (endNodeIndex > 0 ? 1 : 0);

    // Return the range for the match in the original document.
    return {
      anchor: {
        path: startNode.path,
        offset: startOffset,
      },
      focus: {
        path: endNode.path,
        offset: endOffset,
      },
    };
  }

  static isNodeEmpty(editor: Editor, node: Node) {
    for (const [descendant] of Node.descendants(node)) {
      if (Text.isText(descendant) && descendant.text !== '') {
        return false;
      }
      if (!Element.isElement(descendant)) continue;

      if (editor.isInline(descendant) || editor.isVoid(descendant)) {
        return false;
      }
    }

    return true;
  }

  static nextSibling(
    editor: Editor,
    path: Path,
    opts: siblingFindOpts = {}
  ): NodeEntry | undefined {
    if (!PathUtils.hasNext(editor, path)) return;
    const nextSiblingPath = PathUtils.nextSiblingPath(path);
    const [nextSibling] = Editor.node(editor, nextSiblingPath);

    if (!opts.match || opts.match(nextSibling, nextSiblingPath)) {
      return [nextSibling, nextSiblingPath];
    }
  }

  static previousSibling(
    editor: Editor,
    path: Path,
    opts: siblingFindOpts
  ): NodeEntry | undefined {
    if (!PathUtils.hasPrevious(editor, path)) return;

    const previousSiblingPath = PathUtils.previousSiblingPath(path);
    const previousSiblingEntry = Editor.node(editor, previousSiblingPath);
    if (!previousSiblingEntry) return;

    const [previousSibling] = previousSiblingEntry;
    if (!opts.match || opts.match(previousSibling, previousSiblingPath)) {
      return previousSiblingEntry;
    }
  }
}

export default EditorUtils;
